import { useEffect, useState, useCallback, useRef } from "react";
import {
  MDBContainer,
  MDBTabs,
  MDBTabsContent,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsPane,
  MDBRow,
  MDBCol,
  MDBBtn,
} from "mdb-react-ui-kit";
import { useAppContext } from "../../components/AppContext";
import SearchInput from "../../components/SearchInput/SearchInput";
import ManuscriptsTab from "./tabs/ManuscriptsTab";
import ArchivedManuscriptsTab from "./tabs/ArchivedManuscriptsTab";
import { CountCard } from "../../components";
import { showErrorToast, getDuplicateSubmissionsCount } from "../../helpers";
import { getSelectedJournalIds } from "../../helpers/journals";

const TAB_CONFIG = [
  {
    key: "inbox",
    label: "Inbox",
    type: "wo_status",
    component: ManuscriptsTab,
  },
  {
    key: "all",
    label: "All",
    type: "all",
    component: ManuscriptsTab,
  },
  {
    key: "metadata",
    label: "Duplicates",
    type: "metadata",
    component: ManuscriptsTab,
  },
  {
    key: "fts_only",
    label: "FTS",
    type: "fts_only",
    component: ManuscriptsTab,
  },
  {
    key: "archived",
    label: "Archive",
    type: "archived",
    component: ArchivedManuscriptsTab,
  },
];

const DuplicateSubmissions = () => {
  const { setSearchInput, searchInput } = useAppContext();

  const [currentTab, setCurrentTab] = useState(TAB_CONFIG[0].key);
  const [visitedTabs, setVisitedTabs] = useState(new Set([TAB_CONFIG[0].key]));
  const [matchCounts, setMatchCounts] = useState({ metadata: null, fts: null, total: null });
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  const exportFunctionsRef = useRef({});

  useEffect(() => {
    setVisitedTabs((prev) => new Set([...prev, currentTab]));
  }, [currentTab]);

  const handleExport = () => {
    const currentExportFunction = exportFunctionsRef.current[currentTab];
    if (currentExportFunction) {
      currentExportFunction();
    }
  };

  useEffect(() => {
    const fetchCardData = async () => {
      try {
        setIsLoadingCount(true);
        const selectedJournalIds = await getSelectedJournalIds();
        const { data } = await getDuplicateSubmissionsCount(selectedJournalIds);
        setMatchCounts(data);
      } catch (error) {
        console.error(error);
        showErrorToast();
      } finally {
        setIsLoadingCount(false);
      }
    };

    fetchCardData();
  }, []);

  const handleTabClick = useCallback(
    (tabKey) => {
      if (currentTab !== tabKey) {
        setCurrentTab(tabKey);
      }
    },
    [currentTab]
  );

  const handleCounterClick = useCallback(
    (tabKey) => {
      handleTabClick(tabKey);
    },
    [handleTabClick]
  );

  return (
    <MDBContainer className="my-5">
      <div className="d-flex justify-content-center m-5">
        <CountCard
          count={matchCounts.metadata}
          title="Metadata Duplicates"
          subtitle="Current matches"
          onClick={() => handleCounterClick("metadata")}
          isLoading={isLoadingCount}
        />
        <CountCard
          count={matchCounts.fts}
          title="Full-Text Similarity"
          subtitle="Current matches"
          onClick={() => handleCounterClick("fts_only")}
          isLoading={isLoadingCount}
        />
      </div>

      <MDBRow>
        <MDBCol md="auto" className="ms-4">
          <MDBTabs className="mb-3">
            {TAB_CONFIG.map(({ key, label }) => (
              <MDBTabsItem key={key} onClick={() => handleTabClick(key)}>
                <MDBTabsLink active={currentTab === key}>{label}</MDBTabsLink>
              </MDBTabsItem>
            ))}
          </MDBTabs>
        </MDBCol>

        <MDBCol sm={8} md={6} lg={4} className="ms-auto">
          {currentTab === "archived" && (
            <SearchInput
              placeholder="Search by manuscript ID or title"
              onSubmit={setSearchInput}
              initialValue={searchInput}
            />
          )}

          {currentTab !== "archived" && (
            <div className="d-flex justify-content-end mb-3">
              <MDBBtn
                color="secondary"
                className="px-4"
                onClick={handleExport}
                disabled={isLoadingCount}
              >
                Export
              </MDBBtn>
            </div>
          )}
        </MDBCol>
      </MDBRow>

      <MDBTabsContent>
        {TAB_CONFIG.map(({ key, component: TabComponent, type }) => (
          <MDBTabsPane key={key} show={currentTab === key}>
            {visitedTabs.has(key) && (
              <TabComponent
                type={type}
                exportRef={key !== "archived" ? exportFunctionsRef : null}
                tabKey={key}
              />
            )}
          </MDBTabsPane>
        ))}
      </MDBTabsContent>
    </MDBContainer>
  );
};

export default DuplicateSubmissions;